/**
 * "Does this chain of walls split its room?" — the one eligibility test,
 * shared by the client (to decide whether to offer **Split room**) and the
 * server (to refuse before it writes). One implementation so the affordance can
 * never offer something the callable then rejects.
 *
 * A single wall is a chain of one (`findSplitForWall`). Pure, no I/O.
 * Everything it needs is the structure doc and the room docs.
 */

const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const { roomFloorPolygonXZ } = require('./geometry');
const { buildChain, extendChainIds } = require('./chain');
const { cutRoomAlongChain } = require('./chainCut');
const { seatChainEnds } = require('./seatEnds');
const { farSideEvidence, isGuesswork } = require('./evidence');

// An end this far from the room boundary is close enough for the panel to
// offer **Extend to wall** — the placement snap distance. A split itself needs
// the end ON the boundary (`TOUCH_TOL_M`, chain.js): building the room faces
// across a gap the structure wall does not reach leaves a hole in 3D.
const BOUNDARY_TOL_M = 0.3;

/** Rooms on the same storey as `wall`. An unstamped storey matches anything —
 *  an invented `story: 0` is exactly what the storey filter exists to avoid. */
function roomsOnStoreyOf(wall, rooms) {
  const story = wall?.story;
  if (!Number.isFinite(story)) return rooms;
  return rooms.filter((r) => !Number.isFinite(r?.story) || r.story === story);
}

/** Refusal shape — one place so every reason reads the same. */
function no(reason, detail) {
  return { ok: false, reason, ...(detail || {}) };
}

/** The point the containing room is judged by: the middle stretch's middle. */
function chainProbe(chain) {
  const s = chain.stretches[Math.floor((chain.stretches.length - 1) / 2)];
  return { point: { x: (s.a.x + s.b.x) / 2, z: (s.a.z + s.b.z) / 2 }, s };
}

/**
 * Evaluate a chain of walls as a room split.
 *
 * The chain is first extended through any wall whose free end it starts or
 * ends on (spec §4.4). The room is the one — on the chain's storey, never a
 * satellite — containing the middle stretch's midpoint. A room that HOSTS
 * satellites can be split.
 *
 * @param {object} structure structure doc (needs `walls`)
 * @param {object[]} rooms every room doc of the structure
 * @param {string[]} structureWallIds the chain, in any order
 * @param {{ keepSide?: 'left'|'right' }} [opts] which piece keeps the room;
 *   the larger by default
 * @returns {{ ok: true, roomId, room, chain, cut,
 *             kept: { side: 1|-1, polygon, area },
 *             created: { side: 1|-1, polygon, area } }
 *   | { ok: false, reason, …detail }}
 *   Reasons: bad-input, wall-not-found, degenerate-wall, not-a-chain,
 *   no-containing-room, ambiguous-room, endpoint-off-boundary (+ `ends`),
 *   crosses-boundary, bend-on-boundary, multiple-pieces (the wall overlaps the end of another
 *   and would close off a third piece), sliver-side (+ `width`), gap-beyond
 *   (+ `evidence`, only for a chain of two or more walls). Once the room is known a refusal also
 *   carries `roomId` and `chainWallIds`.
 */
function findChainSplit(structure, rooms, structureWallIds, opts = {}) {
  if (
    !structure ||
    !Array.isArray(rooms) ||
    !Array.isArray(structureWallIds) ||
    structureWallIds.length === 0 ||
    structureWallIds.some((id) => !id)
  ) {
    return no('bad-input');
  }
  const walls = structure.walls || [];
  const base = buildChain(walls, structureWallIds);
  if (!base.ok) return no(base.reason);

  const { point, s } = chainProbe(base);
  const probeWall = walls.find((w) => w?.id === s.wallId);
  const candidates = roomsOnStoreyOf(probeWall, rooms).filter((r) => {
    if (r?.parentRoomId) return false; // a satellite is split as part of its host
    const poly = roomFloorPolygonXZ(r);
    return poly.length >= 3 && isPointInsidePolygon(point, poly);
  });
  if (candidates.length === 0) return no('no-containing-room');
  if (candidates.length > 1) return no('ambiguous-room');
  const room = candidates[0];
  const polygon = roomFloorPolygonXZ(room);

  // A chain that starts or ends on a stub's free end is judged with the stub.
  const ids = extendChainIds(walls, base, polygon, probeWall);
  const chain =
    ids.length === base.wallIds.length ? base : buildChain(walls, ids);
  if (!chain.ok) return no(chain.reason);
  const known = { roomId: room.id, chainWallIds: chain.wallIds };

  // an end tapped onto a scanned wall whose face sits a few cm off the
  // outline still reaches it (`seatEnds.js`)
  const cut = cutRoomAlongChain(polygon, seatChainEnds(chain, polygon, walls));
  if (!cut.ok) {
    const { ok: _ok, reason, ...detail } = cut;
    return no(reason, { ...detail, ...known });
  }

  const left = { side: 1, polygon: cut.left.polygon, area: cut.left.area };
  const right = { side: -1, polygon: cut.right.polygon, area: cut.right.area };
  // The LARGER piece keeps the room's identity (id, name, kind, edits); the
  // smaller becomes the new room. `opts.keepSide` lets a caller override.
  let kept = left.area >= right.area ? left : right;
  if (opts.keepSide === 'left') kept = left;
  if (opts.keepSide === 'right') kept = right;
  const created = kept === left ? right : left;

  // A piece that is mostly outline the scan GUESSED is a gap, not a room. A
  // chain of one wall leaves that call to `resolveWallOperation`, which offers
  // Close gap instead; a chain of several has no gap-close (it is one-wall
  // only), so minting a room out of guessed outline is refused here.
  if (chain.wallIds.length > 1) {
    const evidence = farSideEvidence(created.polygon, room, cut.isCutEdge);
    if (isGuesswork(evidence)) return no('gap-beyond', { evidence, ...known });
  }

  return { ok: true, roomId: room.id, room, chain, cut, kept, created };
}

/**
 * Evaluate one wall as a room split: a chain of one (extended through any stub
 * it continues).
 */
function findSplitForWall(structure, rooms, structureWallId, opts = {}) {
  if (!structureWallId) return no('bad-input');
  return findChainSplit(structure, rooms, [structureWallId], opts);
}

module.exports = {
  BOUNDARY_TOL_M,
  findChainSplit,
  findSplitForWall,
};
